import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { motion } from 'framer-motion';

import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage } from '../../../../util/reactIntl';
import { makeGetListingsByIdSelector } from '../../../../ducks/marketplaceData.duck';
import { fetchNewArrivals } from '../../../../ducks/newArrivals.duck';

import { IconSpinner, ListingCard, NamedLink } from '../../../../components';

import SectionContainer from '../../SectionBuilder/SectionContainer';
import SectionTitle from '../SectionTitle';
import {
  MotionSection,
  fadeUp,
  staggerContainer,
  staggerItem,
  viewportOnce,
} from '../scrollAnimations';

import css from './NewArrivalsSection.module.css';

const CARD_SIZES = '(max-width: 767px) 100vw, (max-width: 1024px) 50vw, 25vw';

/**
 * New Arrivals section — shows the 4 most recently created listings.
 * Fetches directly from the Marketplace API on mount; no hosted-asset dependency.
 *
 * Rendered when Page Builder sectionId is `new-arrivals-section` or `new-arrivals`.
 *
 * @component
 * @param {Object} props
 * @param {string} props.sectionId
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {Object?} props.title
 * @param {Object?} props.appearance
 * @param {Object} props.options
 * @returns {JSX.Element}
 */
const NewArrivalsSection = props => {
  const {
    sectionId = 'new-arrivals-section',
    className,
    rootClassName,
    title,
    appearance,
    options,
  } = props;

  const config = useConfiguration();
  const dispatch = useDispatch();

  const getListingsById = useMemo(makeGetListingsByIdSelector, []);

  const { listingIds = [], inProgress, fetched, error } = useSelector(
    state => state.newArrivals || {}
  );
  const listings = useSelector(state => getListingsById(state, listingIds));

  const listingImageConfig = config.layout.listingImage;

  useEffect(() => {
    if (!fetched && !inProgress) {
      dispatch(fetchNewArrivals(listingImageConfig));
    }
    // listingImageConfig is stable for the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fetched, inProgress]);

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(css.root, className)}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <MotionSection>
        <div className={css.inner}>
          <motion.header
            className={css.header}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            {title?.content ? (
              <SectionTitle data={title} className={css.title} />
            ) : (
              <h2 className={css.title}>
                <FormattedMessage
                  id="NewArrivalsSection.defaultTitle"
                  defaultMessage="New Arrivals"
                />
              </h2>
            )}
            <NamedLink
              name="SearchPage"
              to={{ search: '?sort=createdAt&pub_listingType=rent' }}
              className={css.viewAllLink}
            >
              <FormattedMessage id="NewArrivalsSection.viewAll" defaultMessage="VIEW ALL" />
            </NamedLink>
          </motion.header>

          {(!fetched || (inProgress && listings.length === 0)) && !error ? (
            <div className={css.centeredLoading}>
              <IconSpinner />
            </div>
          ) : listings.length > 0 ? (
            <motion.ul
              className={css.grid}
              role="list"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
            >
              {listings.map(listing => (
                <motion.li key={listing.id.uuid} className={css.cardItem} variants={staggerItem}>
                  <ListingCard
                    className={css.card}
                    listing={listing}
                    layout="editorial"
                    renderSizes={CARD_SIZES}
                    showAuthorInfo={false}
                    lazyLoadImage
                  />
                </motion.li>
              ))}
            </motion.ul>
          ) : fetched && !error ? (
            <div className={css.noListings}>
              <p>
                <FormattedMessage
                  id="NewArrivalsSection.noListings"
                  defaultMessage="No listings found."
                />
              </p>
            </div>
          ) : null}
        </div>
      </MotionSection>
    </SectionContainer>
  );
};

export default NewArrivalsSection;
